// node modules
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// ui library
import { View, WarningAlertIcon } from '@upstox/upstox-ui';
// modules
import NotificationBar from 'modules/App/components/NotificationBar';
import { Props as NotificationBarProps } from 'modules/App/components/NotificationBar/typings';
import { useStore } from 'modules/Stores';
import HoldingsService from 'modules/TradesPW3/Holdings/HoldingsService';
import { LStorage } from 'modules/Storage';
import LocaleService from 'modules/Localization';
// utils
import {
  getDiscrepantHoldingsCount,
  shouldShowNotificationAlert,
} from './utils';
// css
import * as css from './styles';

const DiscrepancyIcon: React.FC = () => (
  <View css={css.discrepancyIcon}>
    <WarningAlertIcon color="background.yellow" size="small" />
  </View>
);

const AveragePriceUpdateNotificationFooter: React.FC = () => {
  const [showNotificationAlert, setShowNotificationAlert] = useState(false);

  const { holdings } = useStore('HoldingsStore');
  const { goToExternalRoute } = useStore('RoutingStore');

  const handleUpdateClick = useCallback(() => {
    goToExternalRoute('HOLDING_DISCREPANCY');
    HoldingsService.setHoldingsDiscrepancyAlertReminder();
    setShowNotificationAlert(false);
  }, [goToExternalRoute, setShowNotificationAlert]);

  const handleDismissClick = useCallback(() => {
    HoldingsService.setHoldingsDiscrepancyAlertReminder();
    setShowNotificationAlert(false);
  }, [setShowNotificationAlert]);

  const notificationFooterContent = useMemo(() => {
    const discrepantHoldingsCount = getDiscrepantHoldingsCount(holdings);

    if (showNotificationAlert && discrepantHoldingsCount) {
      const notificationFooterProps: NotificationBarProps = {
        action: {
          buttonText: LocaleService.translate(
            'AVERAGE_PRICE_DISCREPANCY_ACTION',
          ),
          onClick: handleUpdateClick,
        },
        boldText: `${discrepantHoldingsCount} ${
          discrepantHoldingsCount > 1
            ? LocaleService.translate('AVERAGE_PRICE_DISCREPANCY_HOLDINGS')
            : LocaleService.translate('AVERAGE_PRICE_DISCREPANCY_HOLDING')
        }`,
        dismissAction: {
          onClose: handleDismissClick,
        },
        icon: <DiscrepancyIcon />,
        normalText: LocaleService.translate(
          'AVERAGE_PRICE_DISCREPANCY_MESSAGE',
        ),
      };
      return notificationFooterProps;
    }

    if (holdings.length && !discrepantHoldingsCount) {
      LStorage.removeItem('HOLDING_DISCREPANCY_REMINDER');
    }
  }, [handleDismissClick, handleUpdateClick, holdings, showNotificationAlert]);
  useEffect(() => {
    if (shouldShowNotificationAlert()) {
      setShowNotificationAlert(true);
    } else {
      setShowNotificationAlert(false);
    }
  }, [setShowNotificationAlert]);

  if (!notificationFooterContent) {
    return null;
  }

  return <NotificationBar {...notificationFooterContent} />;
};

export default AveragePriceUpdateNotificationFooter;
